/**
 * Typed, dependency-free event emitter.
 *
 * Usage:
 *
 *     const bus = new TypedEventEmitter<{
 *       update: [remaining: number, limit: number];
 *       done: [];
 *     }>();
 *     const dispose = bus.on("update", (remaining, limit) => { ... });
 *     bus.trigger("update", 3, 10);
 *     dispose();                 // or: bus.off("update", handler)
 *
 * `EventMap` keys are event names; values are the argument tuples passed to
 * listeners. This lets `on` / `trigger` type-check argument shapes per-event.
 */
export type EventMapBase = Record<string, unknown[]>;

export type Disposer = () => void;

/**
 * `callback` is what actually runs on `trigger`; `identity` is what
 * `off(event, handler)` compares against. They differ for `once`, where the
 * callback wraps the user's handler to self-remove — but callers still pass the
 * original `handler` to `off`, so we match on `identity`.
 */
type Listener<Args extends unknown[]> = {
  callback: (...args: Args) => void;
  identity: (...args: Args) => void;
};

export class TypedEventEmitter<EventMap extends EventMapBase> {
  private listeners: {
    [K in keyof EventMap]?: Array<Listener<EventMap[K]>>;
  } = {};

  /**
   * Subscribe to `event`. Returns a disposer that removes this exact
   * subscription when called. Callers may tear down either via the returned
   * disposer or via `off(event, handler)` — both styles are supported so
   * migrations from older event emitters can keep their existing shape.
   */
  on<K extends keyof EventMap>(
    event: K,
    handler: (...args: EventMap[K]) => void
  ): Disposer {
    return this.addListener(event, { callback: handler, identity: handler });
  }

  /**
   * Subscribe for a single delivery. The listener removes itself before
   * invoking `handler`, so re-entrant `trigger` calls inside the handler will
   * not re-fire it. Teardown via the returned disposer or `off(event, handler)`
   * (the original handler reference) both work.
   */
  once<K extends keyof EventMap>(
    event: K,
    handler: (...args: EventMap[K]) => void
  ): Disposer {
    const listener: Listener<EventMap[K]> = {
      callback: (...args: EventMap[K]) => {
        this.removeListener(event, listener);
        handler(...args);
      },
      identity: handler,
    };
    return this.addListener(event, listener);
  }

  /**
   * Remove a specific `handler` previously passed to `on` / `once`. No-op if
   * the handler was never registered or the event has no listeners. The
   * `off(event)` form (remove all listeners for an event) is deliberately not
   * supported — no current call-site needs it.
   */
  off<K extends keyof EventMap>(
    event: K,
    handler: (...args: EventMap[K]) => void
  ): void {
    const handlers = this.listeners[event];
    if (!handlers) return;
    this.listeners[event] = handlers.filter(
      (entry) => entry.identity !== handler
    );
  }

  /**
   * Emit `event` to all current listeners, in registration order. The listener
   * list is snapshotted before dispatch so that subscribing or unsubscribing
   * from inside a handler does not affect the in-flight emission — a listener
   * added during `trigger` fires on the *next* emit, and a listener removed
   * during `trigger` still fires for this one. Existing call-sites rely on
   * these snapshot semantics.
   */
  trigger<K extends keyof EventMap>(event: K, ...args: EventMap[K]): void {
    const handlers = this.listeners[event];
    if (!handlers || handlers.length === 0) return;
    const snapshot = handlers.slice();
    for (const entry of snapshot) {
      entry.callback(...args);
    }
  }

  private addListener<K extends keyof EventMap>(
    event: K,
    listener: Listener<EventMap[K]>
  ): Disposer {
    const existing = this.listeners[event] ?? [];
    existing.push(listener);
    this.listeners[event] = existing;
    return () => this.removeListener(event, listener);
  }

  private removeListener<K extends keyof EventMap>(
    event: K,
    listener: Listener<EventMap[K]>
  ): void {
    const handlers = this.listeners[event];
    if (!handlers) return;
    const index = handlers.indexOf(listener);
    if (index === -1) return;
    handlers.splice(index, 1);
  }
}
